import React, { useRef, useCallback, useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { useNavigate } from 'react-router-dom';
import {
    useGetDeclarationByIdQuery,
    useMarkOpenDocumentPrintedMutation,
    useLazyGetChassisDetailQuery,
    useLazyGetDeclarationByIdQuery,
    useLazyGetDeclarationReportByIdQuery,
    useUploadRepairDocumentMutation,
    useLazyGetRepairDocumentsByIdQuery,
    useDeleteRepairDocumentMutation,
    useCloseDeclarationMutation
} from '../../../../../../api/customerDeclarationApi';
import { useLazyDownloadDocumentByKeyQuery } from '../../../../../../api/endpoints/documentContent';
import { useGetChassisDropdownListWithExternalQuery, useGetSapBulletinByChassisNoQuery } from '../../../../../../api/endpoints/compound';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import { usePermissions } from '../../../../../../hooks/usePermissions';
import PdfGenerationService from '../../../../../../utils/pdfGenerationService';
import DocumentDownloadService from '../../../../../../utils/documentDownloadService';
import CustomerDefinitionDialog from '../../../../customer-definition/components/CustomerDefinitionDialog';
import { useAddCustomerMutation, useLazyGetCustomersQuery } from '../../../../../../api/customerApi';
import { useLazyGetWarrantyClaimsListByChassisQuery } from '../../../../../../api/endpoints/warrantyClaims';
import GenericFileUpload from '../../../../../../components/common/GenericFileUpload';
import DetailInfoView from './DetailInfoView';
import DeclarationFormFields from './DeclarationFormFields';
import CloseDeclarationDialog from './CloseDeclarationDialog';
import OpenServiceBulletinsDialog from './OpenServiceBulletinsDialog';
import { getOpeningPdfTranslations, getClosingPdfTranslations, mapClosingFormData } from '../../../utils/pdfHelpers';

const ACCEPTED_FILE_TYPES = {
    'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'text/plain': ['.txt'],
    'application/vnd.ms-outlook': ['.msg'],
    'message/rfc822': ['.eml']
};

const CustomerDeclarationDialog = ({
    visible,
    onHide,
    formData,
    setFormData,
    errors,
    handleSave,
    editMode,
    handleDelete,
    buttonsDisabled = false,
    chassisData,
    setChassisData,
    updatedFiles,
    onFilesChange,
    onRemoveFile
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const toast = useRef(null);
    const { canCreateCustomerDeclaration } = usePermissions();

    const [hasDocumentOperations, setHasDocumentOperations] = useState(false);
    const [isWaitingForDocumentConfirmation, setIsWaitingForDocumentConfirmation] = useState(false);
    const [isDownloadingDocument, setIsDownloadingDocument] = useState(false);
    const [isUpdatingDocument, setIsUpdatingDocument] = useState(false);
    const [isFormDisabled, setIsFormDisabled] = useState(false);
    const [isUploadingFiles, setIsUploadingFiles] = useState(false);
    const [closeDialogVisible, setCloseDialogVisible] = useState(false);
    const [openBulletinsDialogVisible, setOpenBulletinsDialogVisible] = useState(false);

    const [localDocuments, setLocalDocuments] = useState([]);
    const [isLoadingLocalDocuments, setIsLoadingLocalDocuments] = useState(false);

    const prevFormIdRef = useRef(null);
    const prevChassisDetailArgsRef = useRef(null);
    const formDataRef = useRef(formData);

    const [deleteDocumentMutation] = useDeleteRepairDocumentMutation();
    const [closeDeclaration, { isLoading: isClosingDeclaration }] = useCloseDeclarationMutation();

    const [downloadService] = useState(() => new DocumentDownloadService(toast));

    const [triggerGetChassisDetail] = useLazyGetChassisDetailQuery();
    const [triggerGetCustomers] = useLazyGetCustomersQuery();
    const [triggerGetClaimListByChassis] = useLazyGetWarrantyClaimsListByChassisQuery();
    const [markOpenDocumentPrinted] = useMarkOpenDocumentPrintedMutation();
    const [triggerGetDeclarationById, { isLoading: isLoadingPdf }] = useLazyGetDeclarationByIdQuery();
    const [createCustomer, { isLoading: isCreatingCustomer }] = useAddCustomerMutation();
    const [uploadDocumentMutation] = useUploadRepairDocumentMutation();
    const [triggerDownloadByKey] = useLazyDownloadDocumentByKeyQuery();
    const [triggerGetDocuments] = useLazyGetRepairDocumentsByIdQuery();
    const [triggerGetDeclarationReportById] = useLazyGetDeclarationReportByIdQuery();

    const { data: chassisListData } = useGetChassisDropdownListWithExternalQuery({
        LanguageId: 2,
        RowCount: 200,
        SearchText: ''
    }, {
        skip: !visible
    });

    const { data: openBulletins = [], isFetching: isFetchingOpenBulletins } = useGetSapBulletinByChassisNoQuery(
        { ChassisNo: formData?.chassisNumber, OnlyOpen: true },
        { skip: !visible || editMode || !formData?.chassisNumber }
    );

    const [customerDefVisible, setCustomerDefVisible] = useState(false);
    const [customerFormData, setCustomerFormData] = useState({});
    const [customerErrors, setCustomerErrors] = useState({});

    useEffect(() => {
        if (visible) {
            const currentId = formData?.id;
            const prevId = prevFormIdRef.current;

            if (currentId !== prevId) {
                setHasDocumentOperations(false);
                setIsWaitingForDocumentConfirmation(false);
                setIsDownloadingDocument(false);
                setIsUpdatingDocument(false);
                setIsFormDisabled(false);
                setIsUploadingFiles(false);
                setCloseDialogVisible(false);
                setOpenBulletinsDialogVisible(false);
                setLocalDocuments([]);
                setIsLoadingLocalDocuments(false);
                setCustomerDefVisible(false);
                setCustomerFormData({});
                setCustomerErrors({});

                prevFormIdRef.current = currentId;
            }
        }
    }, [visible, formData?.id]);

    useEffect(() => {
        if (!visible) {
            setHasDocumentOperations(false);
            setIsWaitingForDocumentConfirmation(false);
            setIsDownloadingDocument(false);
            setIsUpdatingDocument(false);
            setIsFormDisabled(false);
            setIsUploadingFiles(false);
            setCloseDialogVisible(false);
            setLocalDocuments([]);
            setIsLoadingLocalDocuments(false);
            setCustomerDefVisible(false);
            setCustomerFormData({});
            setCustomerErrors({});
            prevFormIdRef.current = null;
        }
    }, [visible]);

    const fetchDocuments = useCallback(async (declarationId) => {
        if (!declarationId) {
            setLocalDocuments([]);
            return;
        }

        setIsLoadingLocalDocuments(true);
        try {
            const result = await triggerGetDocuments(declarationId).unwrap();
            setLocalDocuments(result || []);
        } catch (error) {
            console.error('DÃ¶kÃ¼man yÃ¼kleme hatasÄ±:', error);
            setLocalDocuments([]);
        } finally {
            setIsLoadingLocalDocuments(false);
        }
    }, [triggerGetDocuments]);

    useEffect(() => {
        if (editMode && formData?.id && visible) {
            fetchDocuments(formData.id);
        } else if (!editMode || !formData?.id) {
            setLocalDocuments([]);
        }
    }, [editMode, formData?.id, visible, fetchDocuments]);

    const addChassis = (chassisNo, licensePlate) => {
        if (!chassisNo) return { success: false, message: t('customerDefinition.validation.chassisRequired') };

        const isDuplicate = customerFormData.chassisList?.some(
            item => item.chassisNo === chassisNo
        );

        if (isDuplicate) {
            return { success: false, message: t('customerDefinition.duplicateChassis') };
        }

        const newChassis = {
            chassisNo: chassisNo,
            licensePlate: licensePlate || ''
        };

        const updatedList = [...(customerFormData.chassisList || []), newChassis];
        setCustomerFormData(prev => ({
            ...prev,
            chassisList: updatedList,
            chassis: '',
            selectedPlate: ''
        }));

        return { success: true };
    };

    const removeChassis = (chassisNo) => {
        const updatedList = customerFormData.chassisList?.filter(
            item => item.chassisNo !== chassisNo
        ) || [];

        setCustomerFormData(prev => ({
            ...prev,
            chassisList: updatedList
        }));
    };

    const { data: declarationData, isLoading: isLoadingDeclaration } = useGetDeclarationByIdQuery(formData?.id, {
        skip: !formData?.id || !visible,
        refetchOnMountOrArgChange: true
    });

    const isClosed = editMode && declarationData?.status === 10;
    const hasWarrantyClaim = editMode && declarationData?.hasWarrantyClaim;
    const isReadOnly = isClosed;
    const canCreateWarrantyRequest = canCreateCustomerDeclaration();
    const isDomestic = declarationData?.isDomestic;

    const declarationDataSyncedIdRef = useRef(null);

    useEffect(() => {
        if (editMode && declarationData && visible && formData?.id && declarationData.id === formData.id && declarationDataSyncedIdRef.current !== formData.id) {
            declarationDataSyncedIdRef.current = formData.id;
            setFormData(prev => ({
                ...prev,
                serviceNote: prev.serviceNote || declarationData.serviceNote || '',
                balanceInfo: prev.balanceInfo || declarationData.bakiyeBilgisi || declarationData.balanceInfo || ''
            }));
        }
        if (!visible) {
            declarationDataSyncedIdRef.current = null;
        }
    }, [editMode, declarationData, visible, formData?.id, setFormData]);

    useEffect(() => {
        if (toast.current) {
            downloadService.toastRef = toast;
        }
    }, [downloadService]);

    useEffect(() => {
        const isProcessing = isDownloadingDocument || isUpdatingDocument || isUploadingFiles || isWaitingForDocumentConfirmation || isClosingDeclaration;
        setIsFormDisabled(isProcessing);
    }, [isDownloadingDocument, isUpdatingDocument, isUploadingFiles, isWaitingForDocumentConfirmation, isClosingDeclaration]);

    // formDataRef her render'da güncel formData'yı taşır — stale closure önleyici
    useEffect(() => { formDataRef.current = formData; });

    const populateFormWithChassisData = useCallback((data, dropdownData = null) => {
        if (!data) return;

        const translatedText = dropdownData?.translatedText || data.translatedText || '';
        const brand = dropdownData?.brand || data.brand || '';
        const brandModel = dropdownData?.brandModel || data.brandModel || '';
        const combinedData = {
            ...data,
            translatedText,
            brand,
            brandModel
        };

        if (setChassisData) setChassisData(combinedData);

        setFormData(prev => ({
            ...prev,
            registrationDate: data.registrationDate ?? null,
            lastKmInfo: data.lastKmInfo ?? 0,
            warrantyBeginDate: data.warrantyBeginDate ?? null,
            warrantyEndDate: data.warrantyEndDate ?? null,
            chassisDescription: prev.chassisDescription || translatedText,
            brand: brand || prev.brand || '',
            model: brandModel || prev.model || '',
            plateNumber: editMode ? prev.plateNumber : (data.plateNumber || prev.plateNumber),
            customer: editMode ? prev.customer : (data.customerId || prev.customer),
            customerPhone: editMode ? prev.customerPhone : (data.customerPhone || prev.customerPhone),
            customerName: editMode ? prev.customerName : (data.customerName || prev.customerName),
            customerNameSurname: editMode ? prev.customerNameSurname : (data.customerName || prev.customerNameSurname)
        }));
    }, [setFormData, setChassisData, editMode]);

    const triggerChassisDetail = useCallback((chassis, km, date) => {
        if (!chassis) return;

        const argsKey = `${chassis}|${km}|${date}`;
        if (prevChassisDetailArgsRef.current === argsKey) return;
        prevChassisDetailArgsRef.current = argsKey;

        let foundDescription = '', brand = '', model = '';
        if (chassisListData && Array.isArray(chassisListData)) {
            const match = chassisListData.find(item => item.id === chassis);
            foundDescription = match?.translatedText || '';
            brand = match?.brand || '';
            model = match?.brandModel || '';
        }

        triggerGetChassisDetail({ chassisNumber: chassis, kmInformation: km, vehicleDate: date })
            .unwrap()
            .then(data => {
                populateFormWithChassisData(data, { translatedText: foundDescription, brand, brandModel: model });
                if (data?.customerId) {
                    toast.current?.show({
                        severity: 'info',
                        summary: t('common.info'),
                        detail: t('warranty.customerDeclaration.messages.customerFound', { name: data.customerName }),
                        life: 4000
                    });
                }
                triggerGetCustomers({ ChassisNo: chassis, PageSize: 50, PageNumber: 1 });
                triggerGetClaimListByChassis({ ChassisNo: chassis, PageNumber: 1, PageSize: 10 });
            })
            .catch(err => {
                console.error('Chassis detail fetch error', err);
                toast.current?.show({
                    severity: 'error',
                    summary: t('common.error'),
                    detail: t('warranty.customerDeclaration.messages.chassisDetailError'),
                    life: 4000
                });
            });
    }, [chassisListData, triggerGetChassisDetail, triggerGetCustomers, triggerGetClaimListByChassis, populateFormWithChassisData, t]);

    // Dialog açılış/kapanış: cleanup ve edit mode otomatik tetikleme
    useEffect(() => {
        if (!visible) {
            prevChassisDetailArgsRef.current = null;
            return;
        }
        const { chassisNumber: c, kmInformation: km, serviceDeliveryDate: d } = formDataRef.current || {};
        triggerChassisDetail(c, km, d);
    }, [visible, triggerChassisDetail]);

    const handleKmChange = useCallback((value) => {
        // State güncellemesi DeclarationFormFields'daki updateField tarafından yapılır
        triggerChassisDetail(
            formDataRef.current?.chassisNumber,
            value,
            formDataRef.current?.serviceDeliveryDate
        );
    }, [triggerChassisDetail]);

    const handleDateChange = useCallback((value) => {
        // State güncellemesi DeclarationFormFields'daki updateField tarafından yapılır
        triggerChassisDetail(
            formDataRef.current?.chassisNumber,
            formDataRef.current?.kmInformation,
            value
        );
    }, [triggerChassisDetail]);

    const handleChassisChange = useCallback((value, chassisDataFromDropdown) => {
        if (editMode && value === formDataRef.current?.chassisNumber) return;

        setChassisData?.(null);

        setFormData(prev => ({
            ...prev,
            chassisNumber: value,
            chassisDescription: chassisDataFromDropdown?.translatedText || '',
            customer: 0,
            customerName: '',
            customerNameSurname: '',
            customerPhone: '',
            plateNumber: '',
            balanceInfo: '',
            lastKmInfo: 0,
            warrantyBeginDate: null,
            warrantyEndDate: null,
            registrationDate: null
        }));

        triggerChassisDetail(
            value,
            formDataRef.current?.kmInformation,
            formDataRef.current?.serviceDeliveryDate
        );
    }, [editMode, setFormData, setChassisData, triggerChassisDetail]);

    const handleCreateWarrantyRequest = (customerStatementId = null) => {
        const statementId = customerStatementId || formData?.id;
        navigate('/warranty-management/warranty-requests/claims/create', {
            state: {
                customerDeclarationId: statementId,
                chassisNumber: formData?.chassisNumber,
                customerId: formData?.customer,
                claimDate: formData?.serviceDeliveryDate ? new Date(formData.serviceDeliveryDate) : null,
                kmInformation: formData?.kmInformation || 0,
                customerDeclarationData: { id: statementId, chassisNumber: formData?.chassisNumber },
                customerStatementId: statementId
            }
        });
    };

    const handleUploadFiles = async (declarationId) => {
        if (!declarationId || !updatedFiles || updatedFiles.length === 0) {
            return;
        }

        setIsUploadingFiles(true);
        try {
            for (const file of updatedFiles) {
                const uploadFormData = new FormData();
                uploadFormData.append('Id', declarationId.toString());
                uploadFormData.append('File', file);
                uploadFormData.append('Description', file.name || 'Document');

                await uploadDocumentMutation(uploadFormData).unwrap();
            }

            if (editMode) {
                await fetchDocuments(declarationId);
            }

            if (onFilesChange) {
                onFilesChange([]);
            }
        } catch (error) {
            console.error('Dosya yÃ¼kleme hatasÄ±:', error);
            const errorMessage = error?.data?.errorMessage || error?.message || t('warranty.customerDeclaration.messages.filesUploadError');
            toast.current?.show({
                severity: 'error',
                summary: t('common.error'),
                detail: errorMessage,
                life: 3000
            });
            throw error;
        } finally {
            setIsUploadingFiles(false);
        }
    };

    const handleValidationSaveAndDownloadDocument = async () => {
        try {
            setIsFormDisabled(true);
            const saved = await handleSave();
            if (!saved?.id) {
                setIsFormDisabled(false);
                return;
            }

            if (updatedFiles && updatedFiles.length > 0) {
                await handleUploadFiles(saved.id);
            }

            await markOpenDocumentPrinted(saved.id).unwrap();
            const result = await triggerGetDeclarationById(saved.id).unwrap();

            if (!result) throw new Error('Document data not found');

            const pdfService = new PdfGenerationService();
            const translations = getOpeningPdfTranslations(t);
            const pdfDoc = pdfService.generateRepairOpeningFormPdf(result, translations);
            pdfService.downloadPdf(pdfDoc, `CustomerDeclaration_${saved.id}.pdf`);

            handleCreateWarrantyRequest(saved.id);

        } catch (error) {
            const errorMessage = error?.data?.errorMessage || t('warranty.customerDeclaration.messages.error');
            toast.current?.show({ severity: 'error', summary: t('common.error'), detail: errorMessage });
        } finally {
            setIsFormDisabled(false);
        }
    };

    const handleSaveWithFiles = async () => {
        try {
            setIsFormDisabled(true);
            const saved = await handleSave();
            if (!saved?.id) {
                setIsFormDisabled(false);
                return;
            }

            if (updatedFiles && updatedFiles.length > 0) {
                await handleUploadFiles(saved.id);
            }

            onHide();
        } catch (error) {
            console.error('Save error:', error);
        } finally {
            setIsFormDisabled(false);
        }
    };

    const handleCloseDeclaration = () => {
        if (!formData?.id) return;
        setCloseDialogVisible(true);
    };

    const handleCloseDeclarationConfirm = async (description) => {
        try {
            await closeDeclaration({ id: formData.id, description }).unwrap();

            setCloseDialogVisible(false);

            toast.current.show({
                severity: 'success',
                summary: t('common.success'),
                detail: t('common.processSuccess'),
                life: 3000
            });

            if (formData.isDomestic) {
                try {
                    const reportResult = await triggerGetDeclarationReportById(formData.id).unwrap();
                    if (reportResult) {
                        const pdfData = mapClosingFormData(reportResult);
                        const translations = getClosingPdfTranslations(t);
                        const pdfService = new PdfGenerationService();
                        const pdfDoc = pdfService.generateRepairClosingFormPdf(pdfData, translations, { halfPage: true });

                        pdfService.downloadPdf(pdfDoc, `RepairClosing_${formData.id}.pdf`);

                    }
                } catch (pdfError) {
                    console.error('Closing PDF generation error:', pdfError);
                }
            }

            setTimeout(() => {
                onHide();
            }, 1000);

        } catch (error) {
            console.error('Onarım kapatma hatası:', error);
            const errorMessage = error?.data?.errorMessage || error?.message || t('warranty.customerDeclaration.messages.error');
            toast.current.show({
                severity: 'error',
                summary: t('common.error'),
                detail: errorMessage,
                life: 4000
            });
        }
    };

    const handleSaveNewCustomer = async () => {
        const isCompany = String(customerFormData.customerType || '1') === '2';
        const newErrors = {};

        if (!customerFormData.firstName) {
            newErrors.firstName = t('common.required');
        }
        if (!customerFormData.phone) {
            newErrors.phone = t('common.required');
        }

        if (!isCompany && (!customerFormData.chassisList || customerFormData.chassisList.length === 0)) {
            newErrors.chassisList = t('customerDefinition.validation.chassisListRequired');
        }

        if (Object.keys(newErrors).length > 0) {
            setCustomerErrors(newErrors);
            return;
        }

        try {
            const result = await createCustomer(customerFormData).unwrap();

            toast.current.show({
                severity: 'success',
                summary: t('common.success'),
                detail: t('customerDefinition.addSuccess')
            });

            setCustomerDefVisible(false);

            if (result?.id) {
                const safePhone = result.phone || result.phoneNumber || customerFormData.phone || '';
                const safePlate = result.plate || result.licensePlate || customerFormData.plate || '';
                const safeName = result.name || `${result.firstName} ${result.lastName}`;

                setFormData(prev => ({
                    ...prev,
                    customer: result.id,
                    customerId: result.id,
                    customerName: safeName,
                    customerNameSurname: safeName,
                    customerPhone: safePhone,
                    plateNumber: safePlate
                }));
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleOpenCustomerDefinition = () => {
        setCustomerFormData({
            customerType: '1',
            country: { name: 'TÃ¼rkiye', code: 'TR' },
            chassis: formData?.chassisNumber || '',
            plate: formData?.plateNumber || '',
            firstName: '',
            lastName: '',
            tcNumber: '',
            phone: '',
            address: '',
            city: '',
            chassisList: [],
            selectedPlate: ''
        });
        setCustomerErrors({});
        setCustomerDefVisible(true);
    };

    const handleUpdateDocument = async (documentData, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (!formData?.id || !documentData.newFile) {
            toast.current?.show({
                severity: 'error',
                summary: t('common.error'),
                detail: t('common.fileNotSelected'),
                life: 3000
            });
            return;
        }

        setIsWaitingForDocumentConfirmation(true);

        if (toast.current) {
            toast.current.show({
                severity: 'warn',
                summary: t('common.info'),
                detail: `'${documentData.fileName}' dosyasini guncellemek istediginizden emin misiniz?`,
                sticky: true,
                closable: false,
                content: (props) => (
                    <div className="flex flex-column align-items-start" style={{ flex: '1' }}>
                        <div className="flex align-items-center gap-2">
                            <span className="font-bold text-900">{props.message.summary}</span>
                        </div>
                        <div className="font-medium text-lg my-3 text-900">{props.message.detail}</div>
                        <div className="flex gap-2">
                            <Button
                                label={t('common.cancel')}
                                onClick={() => {
                                    toast.current.clear();
                                    setIsWaitingForDocumentConfirmation(false);
                                }}
                                className="p-button-sm"
                                severity="secondary"
                            />
                            <Button
                                label={t('warrantyClaims.detail.documents.update')}
                                onClick={async () => {
                                    toast.current.clear();
                                    setIsWaitingForDocumentConfirmation(false);
                                    setIsUpdatingDocument(true);
                                    try {
                                        await deleteDocumentMutation({
                                            formId: formData.id,
                                            documentId: documentData.id
                                        }).unwrap();

                                        const uploadFormData = new FormData();
                                        uploadFormData.append('Id', formData.id.toString());
                                        uploadFormData.append('File', documentData.newFile);
                                        uploadFormData.append('Description', documentData.newFile.name);
                                        await uploadDocumentMutation(uploadFormData).unwrap();

                                        setHasDocumentOperations(true);

                                        await fetchDocuments(formData.id);

                                        toast.current?.show({
                                            severity: 'success',
                                            summary: t('common.success'),
                                            detail: t('warranty.dealerVehicleRegistrationForm.documentManager.toasts.fileUpdated'),
                                            life: 3000
                                        });
                                    } catch (error) {
                                        console.error("Belge gÃ¼ncelleme iÅŸlemi baÅŸarÄ±sÄ±z oldu:", error);
                                        const errorMessage = error?.data?.message || error?.message || 'Bilinmeyen bir hata oluÅŸtu.';
                                        toast.current?.show({
                                            severity: 'error',
                                            summary: t('common.error'),
                                            detail: `${t('common.fileUpdateError')} ${errorMessage}`,
                                            life: 4000
                                        });
                                    } finally {
                                        setIsUpdatingDocument(false);
                                    }
                                }}
                                className="p-button-sm"
                                severity="warning"
                            />
                        </div>
                    </div>
                )
            });
        }
    };

    const handleDownloadDocument = async (documentData, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        setIsDownloadingDocument(true);

        try {
            await downloadService.downloadByKey(
                triggerDownloadByKey,
                documentData.fileKey,
                documentData,
                {
                    successMessage: '',
                    errorMessage: ''
                }
            );
        } catch (error) {
            console.error("Download failed:", error);
        } finally {
            setIsDownloadingDocument(false);
        }
    };

    const renderFooter = () => (
        <div className="flex justify-content-between align-items-center">
            <div className="flex gap-2">
                <Button
                    label={t('warranty.customerDeclaration.dialog.buttons.cancel') || t('common.cancel')}
                    icon="pi pi-times"
                    onClick={onHide}
                    outlined
                    disabled={isLoadingDeclaration || isFormDisabled}
                />

                {editMode && !isClosed && !hasWarrantyClaim && (
                    <Button
                        label={t('warranty.customerDeclaration.dialog.buttons.delete') || t('common.delete')}
                        icon="pi pi-trash"
                        severity="danger"
                        onClick={handleDelete}
                        disabled={isLoadingDeclaration || isFormDisabled}
                    />
                )}
            </div>
            <div className="flex gap-2">
                {editMode && !isClosingDeclaration && !isClosed && (
                    <Button
                        label={t('warranty.customerDeclaration.dialog.buttons.closeDeclaration')}
                        icon="pi pi-lock"
                        severity="warning"
                        onClick={handleCloseDeclaration}
                        disabled={buttonsDisabled || isFormDisabled}
                    />
                )}

                {editMode && canCreateWarrantyRequest && !isClosed && (
                    <Button
                        label={isDomestic ? t('warranty.customerDeclaration.dialog.buttons.createRepairDocument') : t('warranty.customerDeclaration.dialog.buttons.createWarrantyRequest')}
                        icon="pi pi-plus"
                        onClick={() => handleCreateWarrantyRequest()}
                        disabled={buttonsDisabled || isFormDisabled}
                    />
                )}
                {!isClosed && !hasWarrantyClaim && canCreateWarrantyRequest && (
                    <>
                        <Button
                            label={t('warranty.customerDeclaration.dialog.buttons.printDocumentAndCreateRequest')}
                            icon="pi pi-check"
                            onClick={handleValidationSaveAndDownloadDocument}
                            disabled={isLoadingPdf || isFormDisabled}
                        />
                    </>
                )}

                {!isReadOnly && (
                    <Button
                        label={t('warranty.customerDeclaration.dialog.buttons.save') || t('common.save')}
                        icon="pi pi-save"
                        onClick={handleSaveWithFiles}
                        disabled={isLoadingDeclaration || isFormDisabled}
                    />
                )}
            </div>
        </div>
    );

    const renderContent = () => {
        if (isLoadingDeclaration) {
            return <div className="flex justify-content-center p-5"><ProgressSpinner /></div>;
        }

        const processingMessage = isDownloadingDocument
            ? t('common.downloading')
            : isUpdatingDocument
                ? t('common.saving')
                : isUploadingFiles
                    ? t('common.saving')
                    : isWaitingForDocumentConfirmation
                        ? t('common.info')
                        : isClosingDeclaration
                            ? t('common.saving')
                            : '';

        if (isReadOnly || (editMode && !canCreateCustomerDeclaration())) {
            return (
                <>
                    {isFormDisabled && (
                        <div className="flex align-items-center justify-content-center mb-3"
                            style={{
                                background: 'rgba(0,0,0,0.05)',
                                padding: '1rem',
                                borderRadius: '6px'
                            }}
                        >
                            <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" />
                            <span className="ml-3 font-medium">{processingMessage}</span>
                        </div>
                    )}
                    <DetailInfoView
                        formData={formData}
                        chassisData={chassisData}
                        documents={localDocuments}
                        isLoadingDocuments={isLoadingLocalDocuments}
                        onDownloadDocument={handleDownloadDocument}
                    />
                </>
            );
        }

        return (
            <>
                {isFormDisabled && (
                    <div className="flex align-items-center justify-content-center mb-3"
                        style={{
                            background: 'rgba(0,0,0,0.05)',
                            padding: '1rem',
                            borderRadius: '6px'
                        }}
                    >
                        <ProgressSpinner style={{ width: '30px', height: '30px' }} strokeWidth="4" />
                        <span className="ml-3 font-medium">{processingMessage}</span>
                    </div>
                )}

                <div className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
                    <DeclarationFormFields
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        handleChassisChange={handleChassisChange}
                        hasWarrantyClaim={isReadOnly}
                        editMode={editMode}
                        onCreateNewCustomer={handleOpenCustomerDefinition}
                        chassisData={chassisData}
                        onKmChange={handleKmChange}
                        onDateChange={handleDateChange}
                        openBulletinsCount={Array.isArray(openBulletins) ? openBulletins.length : 0}
                        onOpenBulletinsDialog={() => setOpenBulletinsDialogVisible(true)}
                        isFetchingOpenBulletins={isFetchingOpenBulletins}
                    />

                    {editMode && (
                        <div className="mt-4">
                            {hasDocumentOperations && (
                                <div className="mb-3">
                                    <Message
                                        severity="info"
                                        text={t('warranty.vehicleAcceptanceForm.messages.fileChangesAutoSaved')}
                                    />
                                </div>
                            )}

                            <GenericFileUpload
                                newFiles={!isReadOnly ? updatedFiles : []}
                                onFilesChange={!isReadOnly ? onFilesChange : () => { }}
                                existingDocuments={localDocuments}
                                onDownloadDocument={handleDownloadDocument}
                                onDeleteExistingDocument={null}
                                onUpdateExistingDocument={!isReadOnly ? handleUpdateDocument : null}
                                isLoading={isLoadingLocalDocuments}
                                disabled={isReadOnly || isFormDisabled}
                                multiple={true}
                                maxFileSize={20971520}
                                acceptedFileTypes={ACCEPTED_FILE_TYPES}
                                showDownloadButton={true}
                                showUpdateButton={!isReadOnly}
                                showDeleteButton={false}
                                readOnlyMode={isReadOnly}
                            />
                        </div>
                    )}

                    {!editMode && (
                        <div className="mt-4">
                            <div className="field">
                                <label className="font-bold">{t('common.files')}</label>
                                <GenericFileUpload
                                    newFiles={updatedFiles}
                                    onFilesChange={onFilesChange}
                                    existingDocuments={[]}
                                    onDownloadDocument={null}
                                    onDeleteExistingDocument={null}
                                    onUpdateExistingDocument={null}
                                    isLoading={false}
                                    disabled={isFormDisabled}
                                    multiple={true}
                                    maxFileSize={20971520}
                                    acceptedFileTypes={ACCEPTED_FILE_TYPES}
                                    showDownloadButton={false}
                                    showUpdateButton={false}
                                    showDeleteButton={true}
                                    readOnlyMode={false}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={visible}
                style={{ width: '90vw', maxWidth: '900px' }}
                header={`${t('warranty.customerDeclaration.dialog.header')} ${formData?.id ? `#${formData.id}` : ''}`}
                modal
                className="p-fluid"
                footer={renderFooter()}
                onHide={onHide}
            >
                {visible && renderContent()}
            </Dialog>

            <CustomerDefinitionDialog
                visible={customerDefVisible}
                onHide={() => setCustomerDefVisible(false)}
                formData={customerFormData}
                setFormData={setCustomerFormData}
                errors={customerErrors}
                handleSave={handleSaveNewCustomer}
                isSubmitting={isCreatingCustomer}
                editMode={false}
                addChassis={addChassis}
                removeChassis={removeChassis}
            />

            <CloseDeclarationDialog
                visible={closeDialogVisible}
                onHide={() => setCloseDialogVisible(false)}
                onConfirm={handleCloseDeclarationConfirm}
                loading={isClosingDeclaration}
            />

            <OpenServiceBulletinsDialog
                visible={openBulletinsDialogVisible}
                onHide={() => setOpenBulletinsDialogVisible(false)}
                chassisNo={formData?.chassisNumber}
                bulletins={openBulletins}
                isLoading={isFetchingOpenBulletins}
            />
        </>
    );
};

export default React.memo(CustomerDeclarationDialog);